import { useState, FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { authService } from '../../shared/services/auth.service';
import { useApiError } from '../../shared/errors/useApiError';
import { ThemeToggle } from '../../shared/components/ui/ThemeToggle';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { translate } = useApiError();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setSubmitted(true);
    } catch (err) {
      setError(translate(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 4,
        position: 'relative',
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <ThemeToggle />
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('auth.resetPassword')}
        </Typography>

        {submitted ? (
          <Stack spacing={2}>
            <Alert severity="success">{t('auth.resetSuccess')}</Alert>
            <Link component={RouterLink} to="/login" sx={{ color: 'primary.main' }}>
              {t('auth.backToLogin')}
            </Link>
          </Stack>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('auth.resetPasswordSubtitle')}
            </Typography>
            <Stack spacing={2.5}>
              <TextField
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={loading} fullWidth>
                {loading ? t('common.loading') : t('auth.resetPassword')}
              </Button>
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{ color: 'text.secondary' }}
                >
                  {t('auth.backToLogin')}
                </Link>
              </Typography>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
